import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Eye, FilePlus2, Loader2, PencilLine, Plus, Printer, RefreshCw, Save, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { api, BASE, getToken } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';

type EntryType = 'AR' | 'AP' | 'RECEIPT' | 'PAYMENT';
type Mode = 'create' | 'view' | 'edit';

type LedgerEntry = {
  id: string;
  entryNumber?: string | null;
  entry_number?: string | null;
  entryType?: EntryType | string | null;
  entry_type?: EntryType | string | null;
  jobId?: string | null;
  job_id?: string | null;
  sourceType?: string | null;
  source_type?: string | null;
  sourceRef?: string | null;
  source_ref?: string | null;
  status?: string | null;
  currency?: string | null;
  totalAmount?: number | string | null;
  total_amount?: number | string | null;
  paidAmount?: number | string | null;
  paid_amount?: number | string | null;
  entryDate?: string | null;
  entry_date?: string | null;
  dueDate?: string | null;
  due_date?: string | null;
  narration?: string | null;
  data?: unknown;
  customerId?: string | null;
  customer_id?: string | null;
  vendorId?: string | null;
  vendor_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type LedgerLine = {
  id: string;
  lineNo?: number | string | null;
  line_no?: number | string | null;
  chargeCodeId?: string | null;
  charge_code_id?: string | null;
  description?: string | null;
  quantity?: number | string | null;
  unitPrice?: number | string | null;
  unit_price?: number | string | null;
  taxRate?: number | string | null;
  tax_rate?: number | string | null;
  amount?: number | string | null;
  taxAmount?: number | string | null;
  tax_amount?: number | string | null;
};

type LedgerDetail = LedgerEntry & {
  lines?: LedgerLine[];
};

type LineDraft = {
  id: string;
  persisted: boolean;
  deleted: boolean;
  chargeCodeId: string;
  description: string;
  quantity: string;
  unitPrice: string;
  taxRate: string;
};

type EditorDraft = {
  entryNumber: string;
  partyId: string;
  partyName: string;
  partyCode: string;
  jobId: string;
  jobNo: string;
  entryDate: string;
  dueDate: string;
  currency: string;
  narration: string;
};

type SimpleAccountingConfig = {
  kind: 'invoice' | 'payable' | 'receipt' | 'payment';
  title: string;
  subtitle: string;
  Icon: LucideIcon;
  iconClassName?: string;
  entryType: EntryType;
  newLabel: string;
  partyLabel: string;
  documentLabel: string;
  amountLabel: string;
  accent: 'blue' | 'orange' | 'green' | 'red';
};

type Paginated<T> = {
  items?: T[];
  total?: number;
};

type PartyOption = {
  id: string;
  name?: string | null;
  code?: string | null;
  contact_email?: string | null;
  email?: string | null;
};

type ChargeCode = {
  id: string;
  code?: string | null;
  description?: string | null;
  is_active?: boolean | null;
};

type JobOption = {
  id: string;
  job_number?: string | null;
  jobNumber?: string | null;
  customer_name?: string | null;
  customerName?: string | null;
  status?: string | null;
};

const EMPTY_EDITOR: EditorDraft = {
  entryNumber: '',
  partyId: '',
  partyName: '',
  partyCode: '',
  jobId: '',
  jobNo: '',
  entryDate: '',
  dueDate: '',
  currency: 'SGD',
  narration: '',
};

const STATUS_STYLE: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700',
  posted: 'bg-blue-100 text-blue-700',
  paid: 'bg-emerald-100 text-emerald-700',
  partial_paid: 'bg-amber-100 text-amber-700',
  void: 'bg-rose-100 text-rose-700',
  reversed: 'bg-zinc-100 text-zinc-700',
};

const ACCENT_CLASS: Record<SimpleAccountingConfig['accent'], string> = {
  blue: 'border-blue-200 bg-blue-50 text-blue-700',
  orange: 'border-orange-200 bg-orange-50 text-orange-700',
  green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  red: 'border-rose-200 bg-rose-50 text-rose-700',
};

function asNumber(value: unknown): number {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function parseData(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function firstValue(source: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

function asOptionalString(value: unknown): string | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  return String(value);
}

function normalizeLedgerDetail(raw: unknown, fallbackId: string): LedgerDetail {
  const container = asRecord(raw);
  const source = asRecord(container.entry).id ? asRecord(container.entry) : container;
  const rawLines = Array.isArray(source.lines) ? source.lines : Array.isArray(container.lines) ? container.lines : [];
  return {
    ...(source as LedgerEntry),
    id: String(firstValue(source, 'id') ?? fallbackId),
    entry_number: asOptionalString(firstValue(source, 'entry_number', 'entryNumber')),
        entry_type: asOptionalString(firstValue(source, 'entry_type', 'entryType')) as EntryType | string | null | undefined,
        job_id: asOptionalString(firstValue(source, 'job_id', 'jobId')),
        source_type: asOptionalString(firstValue(source, 'source_type', 'sourceType')),
        source_ref: asOptionalString(firstValue(source, 'source_ref', 'sourceRef')),
        status: asOptionalString(firstValue(source, 'status')) ?? 'draft',
    currency: asOptionalString(firstValue(source, 'currency')) ?? 'SGD',
    total_amount: firstValue(source, 'total_amount', 'totalAmount') as number | string | null | undefined,
    paid_amount: firstValue(source, 'paid_amount', 'paidAmount') as number | string | null | undefined,
    entry_date: asOptionalString(firstValue(source, 'entry_date', 'entryDate')),
    due_date: asOptionalString(firstValue(source, 'due_date', 'dueDate')),
    narration: asOptionalString(firstValue(source, 'narration')),
    customer_id: asOptionalString(firstValue(source, 'customer_id', 'customerId')),
    vendor_id: asOptionalString(firstValue(source, 'vendor_id', 'vendorId')),
    data: firstValue(source, 'data'),
    lines: rawLines.map((line, index) => {
      const row = asRecord(line);
      return {
        ...(row as LedgerLine),
        id: String(firstValue(row, 'id') ?? `line-${index + 1}`),
        line_no: firstValue(row, 'line_no', 'lineNo') as number | string | null | undefined,
        charge_code_id: asOptionalString(firstValue(row, 'charge_code_id', 'chargeCodeId')),
        description: asOptionalString(firstValue(row, 'description')),
        quantity: firstValue(row, 'quantity') as number | string | null | undefined,
        unit_price: firstValue(row, 'unit_price', 'unitPrice') as number | string | null | undefined,
        tax_rate: firstValue(row, 'tax_rate', 'taxRate') as number | string | null | undefined,
        amount: firstValue(row, 'amount') as number | string | null | undefined,
        tax_amount: firstValue(row, 'tax_amount', 'taxAmount') as number | string | null | undefined,
      };
    }),
  };
}

function money(value: unknown, currency = 'SGD'): string {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(asNumber(value));
}

function dateOnly(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  return date.toISOString().slice(0, 10);
}

function displayDate(value?: string | null): string {
  const normalized = dateOnly(value);
  if (!normalized) return '-';
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return normalized;
  return new Intl.DateTimeFormat('en-SG', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
}

function lineToDraft(line: LedgerLine): LineDraft {
  return {
    id: line.id,
    persisted: true,
    deleted: false,
    chargeCodeId: line.charge_code_id ?? line.chargeCodeId ?? '',
    description: line.description ?? '',
    quantity: String(line.quantity ?? 1),
    unitPrice: String(line.unit_price ?? line.unitPrice ?? line.amount ?? 0),
    taxRate: String(line.tax_rate ?? line.taxRate ?? 0),
  };
}

function createEmptyLine(): LineDraft {
  return {
    id: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    persisted: false,
    deleted: false,
    chargeCodeId: '',
    description: 'Service line',
    quantity: '1',
    unitPrice: '0',
    taxRate: '0',
  };
}

function partyName(entry: LedgerEntry): string {
  const data = parseData(entry.data);
  return String(data.partyName ?? data.party_name ?? data.customerName ?? data.vendorName ?? entry.narration ?? 'Not tagged');
}

function partyId(entry: LedgerEntry): string {
  const data = parseData(entry.data);
  return String(entry.customer_id ?? entry.customerId ?? entry.vendor_id ?? entry.vendorId ?? data.partyId ?? data.party_id ?? '');
}

function jobId(entry: LedgerEntry): string {
  const data = parseData(entry.data);
  return String(entry.job_id ?? entry.jobId ?? data.jobId ?? data.job_id ?? '');
}

function jobNo(entry: LedgerEntry): string {
  const data = parseData(entry.data);
  return String(data.jobNo ?? data.jobNumber ?? data.job_no ?? data.job_number ?? '');
}

function optionName(option: PartyOption): string {
  return String(option.name ?? option.code ?? option.id);
}

function optionCode(option: PartyOption): string {
  return String(option.code ?? '');
}

function optionLabel(option: PartyOption): string {
  const code = optionCode(option);
  const name = optionName(option);
  return code && code !== name ? `${code} - ${name}` : name;
}

function optionSubLabel(option: PartyOption): string {
  return [option.contact_email ?? option.email, option.id].filter(Boolean).join(' · ');
}

function chargeCodeLabel(option: ChargeCode): string {
  const code = String(option.code ?? option.id);
  return option.description ? `${code} - ${option.description}` : code;
}

function jobOptionNo(option: JobOption): string {
  return String(option.job_number ?? option.jobNumber ?? option.id);
}

function jobOptionLabel(option: JobOption): string {
  const no = jobOptionNo(option);
  const customer = String(option.customer_name ?? option.customerName ?? '');
  return customer ? `${no} - ${customer}` : no;
}

function generateEntryNumber(entryType: EntryType): string {
  const prefix: Record<EntryType, string> = {
    AR: 'INV',
    AP: 'BILL',
    RECEIPT: 'RCPT',
    PAYMENT: 'PMT',
  };
  const now = new Date();
  const stamp = now.toISOString().slice(0, 10).replace(/-/g, '');
  const seq = String(now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()).padStart(5, '0');
  return `${prefix[entryType]}-${stamp}-${seq}`;
}

function isLocked(status?: string | null): boolean {
  return ['posted', 'paid', 'partial_paid', 'void', 'reversed', 'written_off'].includes(String(status ?? '').toLowerCase());
}

function statusLabel(status?: string | null): string {
  const value = String(status ?? 'draft').toLowerCase();
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function invoicePrintUrl(entryId: string, autoPrint = false): string {
  const params = new URLSearchParams();
  const token = getToken();
  if (token) params.set('token', token);
  if (autoPrint) params.set('auto', '1');
  const query = params.toString();
  return `${BASE}/invoices/${encodeURIComponent(entryId)}/preview${query ? `?${query}` : ''}`;
}

export function SimpleAccountingPage({ config }: { config: SimpleAccountingConfig }) {
  const toast = useToast();
  const [items, setItems] = useState<LedgerEntry[]>([]);
  const [partyOptions, setPartyOptions] = useState<PartyOption[]>([]);
  const [partyLoading, setPartyLoading] = useState(false);
  const [jobOptions, setJobOptions] = useState<JobOption[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [chargeCodes, setChargeCodes] = useState<ChargeCode[]>([]);
  const [chargeCodesLoading, setChargeCodesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<LedgerDetail | null>(null);
  const [mode, setMode] = useState<Mode>('view');
  const [editor, setEditor] = useState<EditorDraft>(EMPTY_EDITOR);
  const [lines, setLines] = useState<LineDraft[]>([]);
  const isSupplierDocument = config.entryType === 'AP' || config.entryType === 'PAYMENT';

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        entryType: config.entryType,
        limit: '100',
        sort: 'updated_at',
        order: 'desc',
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const response = await api.get<Paginated<LedgerEntry>>(`/ledger-entries?${params.toString()}`);
      setItems(response.items ?? []);
    } catch (error) {
      setItems([]);
      toast.error(error instanceof Error ? error.message : `Failed to load ${config.title}`);
    } finally {
      setLoading(false);
    }
  }, [config.entryType, config.title, statusFilter, toast]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const loadPartyOptions = useCallback(async () => {
    setPartyLoading(true);
    try {
      const endpoint = isSupplierDocument ? '/scm/suppliers' : '/customers';
      const response = await api.get<Paginated<PartyOption>>(`${endpoint}?limit=100&page=1&sort=name&order=asc`);
      setPartyOptions(response.items ?? []);
    } catch (error) {
      setPartyOptions([]);
      toast.error(error instanceof Error ? error.message : `Failed to load ${config.partyLabel} list`);
    } finally {
      setPartyLoading(false);
    }
  }, [config.partyLabel, isSupplierDocument, toast]);

  useEffect(() => {
    void loadPartyOptions();
  }, [loadPartyOptions]);

  const loadJobOptions = useCallback(async () => {
    setJobsLoading(true);
    try {
      const response = await api.get<Paginated<JobOption>>('/jobs?limit=200&page=1&sort=created_at&order=desc');
      setJobOptions(response.items ?? []);
    } catch {
      setJobOptions([]);
    } finally {
      setJobsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadJobOptions();
  }, [loadJobOptions]);

  const loadChargeCodes = useCallback(async () => {
    setChargeCodesLoading(true);
    try {
      const response = await api.get<ChargeCode[] | Paginated<ChargeCode>>('/charge-codes?limit=100&page=1&sort=code&order=asc');
      const items = Array.isArray(response) ? response : response.items ?? [];
      setChargeCodes(items.filter((item) => item.is_active !== false));
    } catch (error) {
      setChargeCodes([]);
      toast.error(error instanceof Error ? error.message : 'Failed to load charge codes');
    } finally {
      setChargeCodesLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadChargeCodes();
  }, [loadChargeCodes]);

  const openEntry = useCallback(async (id: string) => {
    setSelectedId(id);
    setMode('view');
    setDetail(null);
    try {
      const next = normalizeLedgerDetail(await api.get<unknown>(`/ledger-entries/${id}`), id);
      setDetail(next);
      const data = parseData(next.data);
      setEditor({
        entryNumber: next.entry_number ?? '',
        partyId: partyId(next),
        partyName: partyName(next),
        partyCode: String(data.partyCode ?? data.party_code ?? ''),
        jobId: jobId(next),
        jobNo: jobNo(next),
        entryDate: dateOnly(next.entry_date),
        dueDate: dateOnly(next.due_date),
        currency: next.currency ?? 'SGD',
        narration: next.narration ?? '',
      });
      setLines((next.lines ?? []).map(lineToDraft));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : `Failed to open ${config.documentLabel}`);
      setSelectedId(null);
      setDetail(null);
    }
  }, [config.documentLabel, toast]);

  const startCreate = () => {
    const today = new Date().toISOString().slice(0, 10);
    setSelectedId(null);
    setDetail(null);
    setMode('create');
    setEditor({
      entryNumber: generateEntryNumber(config.entryType),
      partyId: '',
      partyName: '',
      partyCode: '',
      jobId: '',
      jobNo: '',
      entryDate: today,
      dueDate: config.entryType === 'AR' || config.entryType === 'AP' ? today : '',
      currency: 'SGD',
      narration: '',
    });
    setLines([{ ...createEmptyLine(), description: config.documentLabel }]);
  };

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => [
      item.entry_number,
      item.status,
      item.narration,
      partyName(item),
      jobNo(item),
      item.currency,
    ].some((value) => String(value ?? '').toLowerCase().includes(needle)));
  }, [items, search]);

  const totals = useMemo(() => {
    const openAmount = filtered.reduce((sum, item) => sum + Math.max(0, asNumber(item.total_amount) - asNumber(item.paid_amount)), 0);
    const totalAmount = filtered.reduce((sum, item) => sum + asNumber(item.total_amount), 0);
    const draftCount = filtered.filter((item) => String(item.status ?? 'draft').toLowerCase() === 'draft').length;
    const overdueCount = filtered.filter((item) => {
      const due = dateOnly(item.due_date);
      return due && due < new Date().toISOString().slice(0, 10) && !isLocked(item.status);
    }).length;
    return { openAmount, totalAmount, draftCount, overdueCount };
  }, [filtered]);

  const activeLines = lines.filter((line) => !line.deleted);
  const draftSubtotal = activeLines.reduce((sum, line) => sum + asNumber(line.quantity) * asNumber(line.unitPrice), 0);
  const draftTax = activeLines.reduce((sum, line) => sum + asNumber(line.quantity) * asNumber(line.unitPrice) * asNumber(line.taxRate) / 100, 0);
  const canEditLines = mode === 'create' || (mode === 'edit' && !isLocked(detail?.status));
  const partySelectOptions = useMemo(() => {
    if (!editor.partyId || partyOptions.some((option) => option.id === editor.partyId)) return partyOptions;
    return [{ id: editor.partyId, name: editor.partyName, code: editor.partyCode }, ...partyOptions];
  }, [editor.partyCode, editor.partyId, editor.partyName, partyOptions]);
  const jobSelectOptions = useMemo(() => {
    if (!editor.jobId || jobOptions.some((option) => option.id === editor.jobId)) return jobOptions;
    return [{ id: editor.jobId, job_number: editor.jobNo || editor.jobId }, ...jobOptions];
  }, [editor.jobId, editor.jobNo, jobOptions]);
  const chargeCodeOptions = useMemo(() => {
    const byId = new Map(chargeCodes.map((code) => [code.id, code]));
    for (const line of activeLines) {
      if (!line.chargeCodeId || byId.has(line.chargeCodeId)) continue;
      byId.set(line.chargeCodeId, {
        id: line.chargeCodeId,
        code: line.chargeCodeId,
        description: line.description || null,
        is_active: true,
      });
    }
    return Array.from(byId.values()).sort((a, b) => String(a.code ?? a.id).localeCompare(String(b.code ?? b.id)));
  }, [activeLines, chargeCodes]);

  const updateLine = (id: string, patch: Partial<LineDraft>) => {
    setLines((current) => current.map((line) => (line.id === id ? { ...line, ...patch } : line)));
  };

  const removeLine = (id: string) => {
    setLines((current) => current
      .map((line) => (line.id === id ? { ...line, deleted: true } : line))
      .filter((line) => line.persisted || !line.deleted));
  };

  const saveEntry = async () => {
    setSaving(true);
    try {
      const party = partySelectOptions.find((option) => option.id === editor.partyId);
      const selectedPartyName = party ? optionName(party) : editor.partyName.trim();
      const selectedPartyCode = party ? optionCode(party) : editor.partyCode.trim();
      const partyPayload = isSupplierDocument
        ? { vendorId: editor.partyId || undefined }
        : { customerId: editor.partyId || undefined };
      const partyData = {
        partyId: editor.partyId || undefined,
        partyName: selectedPartyName || undefined,
        partyCode: selectedPartyCode || undefined,
        partyRole: isSupplierDocument ? 'supplier' : 'customer',
      };
      const job = jobSelectOptions.find((option) => option.id === editor.jobId);
      const selectedJobNo = job ? jobOptionNo(job) : editor.jobNo.trim();
      const jobData = {
        jobId: editor.jobId || undefined,
        jobNo: selectedJobNo || undefined,
        jobNumber: selectedJobNo || undefined,
      };
      if (!editor.partyId) {
        toast.error(`Select ${config.partyLabel} before saving`);
        return;
      }
      const missingChargeLine = activeLines.find((line) => !line.chargeCodeId);
      if (missingChargeLine) {
        toast.error('Select charge code for every line');
        return;
      }

      if (mode === 'create') {
        const created = await api.post<LedgerEntry>('/ledger-entries', {
          entryNumber: editor.entryNumber.trim() || generateEntryNumber(config.entryType),
          entryType: config.entryType,
          jobId: editor.jobId || undefined,
          ...partyPayload,
          currency: editor.currency || 'SGD',
          exchangeRate: 1,
          entryDate: editor.entryDate || undefined,
          dueDate: editor.dueDate || undefined,
          sourceType: editor.jobId ? 'job' : undefined,
          sourceRef: editor.jobId ? `job:${editor.jobId}` : undefined,
          narration: editor.narration.trim() || undefined,
          data: { ...partyData, ...jobData },
        });
        for (const line of activeLines) {
          if (!line.description.trim()) continue;
          await api.post(`/ledger-entries/${created.id}/lines`, {
            chargeCodeId: line.chargeCodeId,
            description: line.description.trim(),
            quantity: asNumber(line.quantity) || 1,
            unitPrice: asNumber(line.unitPrice),
            taxRate: asNumber(line.taxRate),
          });
        }
        toast.success(`${config.documentLabel} created`);
        await loadList();
        await openEntry(created.id);
        return;
      }

      if (!detail?.id) return;
      await api.patch(`/ledger-entries/${detail.id}`, {
        ...partyPayload,
        jobId: editor.jobId || null,
        sourceType: editor.jobId ? 'job' : null,
        sourceRef: editor.jobId ? `job:${editor.jobId}` : null,
        dueDate: editor.dueDate || undefined,
        narration: editor.narration,
        data: { ...partyData, ...jobData },
      });

      if (!isLocked(detail.status)) {
        for (const line of lines) {
          if (line.persisted && line.deleted) {
            await api.delete(`/ledger-entries/${detail.id}/lines/${line.id}`);
            continue;
          }
          if (line.deleted || !line.description.trim()) continue;
          const payload = {
            chargeCodeId: line.chargeCodeId,
            description: line.description.trim(),
            quantity: asNumber(line.quantity) || 1,
            unitPrice: asNumber(line.unitPrice),
            taxRate: asNumber(line.taxRate),
          };
          if (line.persisted) {
            await api.patch(`/ledger-entries/${detail.id}/lines/${line.id}`, payload);
          } else {
            await api.post(`/ledger-entries/${detail.id}/lines`, payload);
          }
        }
      }

      toast.success(`${config.documentLabel} updated`);
      await loadList();
      await openEntry(detail.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : `Failed to save ${config.documentLabel}`);
    } finally {
      setSaving(false);
    }
  };

  const closeSheet = () => {
    setSelectedId(null);
    setDetail(null);
    setMode('view');
    setLines([]);
    setEditor(EMPTY_EDITOR);
  };

  const sheetOpen = mode === 'create' || Boolean(selectedId);
  const activeStatus = mode === 'create' ? 'draft' : detail?.status ?? 'draft';

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <config.Icon className={cn('h-5 w-5', config.iconClassName)} />
            <h2 className="text-lg font-semibold">{config.title}</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{config.subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void loadList()} disabled={loading}>
            <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', loading && 'animate-spin')} />
            Refresh
          </Button>
          <Button size="sm" onClick={startCreate}>
            <FilePlus2 className="mr-1.5 h-3.5 w-3.5" />
            {config.newLabel}
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Kpi label={config.documentLabel} value={String(filtered.length)} />
        <Kpi label={config.amountLabel} value={money(config.entryType === 'RECEIPT' || config.entryType === 'PAYMENT' ? totals.totalAmount : totals.openAmount)} tone={config.accent} />
        <Kpi label="Draft" value={String(totals.draftCount)} />
        <Kpi label="Overdue / Attention" value={String(totals.overdueCount)} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="min-w-[260px] max-w-xl"
          placeholder="Search number, party, memo..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {['all', 'draft', 'posted', 'partial_paid', 'paid', 'void'].map((status) => (
          <Button
            key={status}
            size="sm"
            variant={statusFilter === status ? 'default' : 'outline'}
            onClick={() => setStatusFilter(status)}
          >
            {status === 'all' ? 'All' : statusLabel(status)}
          </Button>
        ))}
      </div>

      <div className="rounded-md border bg-background">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="text-sm font-semibold">{config.title} List</div>
          <div className="text-xs text-muted-foreground">{filtered.length} row(s)</div>
        </div>
        <div className="h-[62vh] overflow-auto">
          <table className="min-w-[1120px] text-sm">
            <thead className="sticky top-0 z-10 bg-muted/80 backdrop-blur">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium">No</th>
                <th className="px-3 py-2 font-medium">Job No</th>
                <th className="px-3 py-2 font-medium">{config.partyLabel}</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Due</th>
                <th className="px-3 py-2 font-medium">Currency</th>
                <th className="px-3 py-2 text-right font-medium">Total</th>
                <th className="px-3 py-2 text-right font-medium">Paid</th>
                <th className="px-3 py-2 text-right font-medium">Open</th>
                <th className="px-3 py-2 font-medium">Memo</th>
                <th className="px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={12} className="px-3 py-12 text-center text-muted-foreground">
                    <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                    Loading...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-3 py-12 text-center text-muted-foreground">No records found.</td>
                </tr>
              ) : filtered.map((item) => {
                const total = asNumber(item.total_amount);
                const paid = asNumber(item.paid_amount);
                const open = Math.max(0, total - paid);
                const currency = item.currency ?? 'SGD';
                return (
                  <tr key={item.id} className="cursor-pointer border-t hover:bg-muted/40" onClick={() => void openEntry(item.id)}>
                    <td className="px-3 py-2 font-medium">{item.entry_number ?? item.id}</td>
                    <td className="px-3 py-2 font-mono text-xs">{jobNo(item) || '-'}</td>
                    <td className="px-3 py-2">{partyName(item)}</td>
                    <td className="px-3 py-2">
                      <Badge className={cn('border-0', STATUS_STYLE[String(item.status ?? 'draft').toLowerCase()] ?? 'bg-slate-100 text-slate-700')}>
                        {statusLabel(item.status)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">{displayDate(item.entry_date)}</td>
                    <td className="px-3 py-2">{displayDate(item.due_date)}</td>
                    <td className="px-3 py-2">{currency}</td>
                    <td className="px-3 py-2 text-right font-semibold">{money(total, currency)}</td>
                    <td className="px-3 py-2 text-right">{money(paid, currency)}</td>
                    <td className="px-3 py-2 text-right">{money(open, currency)}</td>
                    <td className="max-w-[260px] truncate px-3 py-2 text-muted-foreground">{item.narration ?? '-'}</td>
                    <td className="px-3 py-2">
                      <Button size="sm" variant="outline" onClick={(event) => { event.stopPropagation(); void openEntry(item.id); }}>
                        View / Edit
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Sheet open={sheetOpen} onOpenChange={(open) => { if (!open) closeSheet(); }}>
        <SheetContent side="right" className="w-[1080px] max-w-[96vw] overflow-y-auto p-0">
          <SheetHeader className="border-b px-5 py-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <SheetTitle>{mode === 'create' ? config.newLabel : `${config.documentLabel} : ${detail?.entry_number ?? ''}`}</SheetTitle>
                <div className="mt-2 flex items-center gap-2">
                  <Badge className={cn('border-0', STATUS_STYLE[String(activeStatus).toLowerCase()] ?? 'bg-slate-100 text-slate-700')}>
                    {statusLabel(activeStatus)}
                  </Badge>
                  {isLocked(activeStatus) ? <Badge variant="outline">Locked</Badge> : <Badge variant="outline">Editable draft</Badge>}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {(config.entryType === 'AR' || config.entryType === 'AP') && (detail?.id ?? selectedId) ? (
                  <>
                    <Button size="sm" variant="outline" onClick={() => window.open(invoicePrintUrl(String(detail?.id ?? selectedId)), '_blank', 'noopener,noreferrer')}>
                      <Eye className="mr-1.5 h-3.5 w-3.5" />
                      Preview
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => window.open(invoicePrintUrl(String(detail?.id ?? selectedId), true), '_blank', 'noopener,noreferrer')}>
                      <Printer className="mr-1.5 h-3.5 w-3.5" />
                      Print
                    </Button>
                  </>
                ) : null}
                {mode === 'view' ? (
                  <Button size="sm" variant="outline" onClick={() => setMode('edit')}>
                    <PencilLine className="mr-1.5 h-3.5 w-3.5" />
                    Edit
                  </Button>
                ) : null}
                {mode !== 'view' ? (
                  <Button size="sm" onClick={() => void saveEntry()} disabled={saving}>
                    {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
                    Save
                  </Button>
                ) : null}
              </div>
            </div>
          </SheetHeader>

          <div className="space-y-4 px-5 py-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <Field label="No">
                <Input disabled={mode !== 'create'} value={editor.entryNumber} onChange={(event) => setEditor((prev) => ({ ...prev, entryNumber: event.target.value }))} />
              </Field>
              <Field label={config.partyLabel}>
                <Select
                  disabled={mode === 'view' || partyLoading}
                  value={editor.partyId || undefined}
                  onValueChange={(value) => {
                    const party = partySelectOptions.find((option) => option.id === value);
                    setEditor((prev) => ({
                      ...prev,
                      partyId: value,
                      partyName: party ? optionName(party) : prev.partyName,
                      partyCode: party ? optionCode(party) : prev.partyCode,
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={partyLoading ? `Loading ${config.partyLabel.toLowerCase()}...` : `Select ${config.partyLabel.toLowerCase()}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {partySelectOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        <div className="min-w-0">
                          <div className="truncate">{optionLabel(option)}</div>
                          <div className="truncate text-xs text-muted-foreground">{optionSubLabel(option)}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Job No">
                <Select
                  disabled={mode === 'view' || jobsLoading}
                  value={editor.jobId || undefined}
                  onValueChange={(value) => {
                    if (value === '__none') {
                      setEditor((prev) => ({ ...prev, jobId: '', jobNo: '' }));
                      return;
                    }
                    const job = jobSelectOptions.find((option) => option.id === value);
                    setEditor((prev) => ({
                      ...prev,
                      jobId: value,
                      jobNo: job ? jobOptionNo(job) : prev.jobNo,
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={jobsLoading ? 'Loading jobs...' : 'Select job'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">No linked job</SelectItem>
                    {jobSelectOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        <div className="min-w-0">
                          <div className="truncate">{jobOptionLabel(option)}</div>
                          <div className="truncate text-xs text-muted-foreground">{option.status ?? option.id}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Date">
                <Input disabled={mode !== 'create'} type="date" value={editor.entryDate} onChange={(event) => setEditor((prev) => ({ ...prev, entryDate: event.target.value }))} />
              </Field>
              <Field label="Due Date">
                <Input disabled={mode === 'view'} type="date" value={editor.dueDate} onChange={(event) => setEditor((prev) => ({ ...prev, dueDate: event.target.value }))} />
              </Field>
              <Field label="Currency">
                <Input disabled={mode !== 'create'} value={editor.currency} onChange={(event) => setEditor((prev) => ({ ...prev, currency: event.target.value.toUpperCase().slice(0, 3) }))} />
              </Field>
              <div className="min-w-0 space-y-2 md:col-span-3">
                <Label>Memo</Label>
                <Textarea disabled={mode === 'view'} rows={2} value={editor.narration} onChange={(event) => setEditor((prev) => ({ ...prev, narration: event.target.value }))} />
              </div>
            </div>

            <div className="rounded-md border">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
                <div className="text-sm font-semibold">Lines</div>
                <Button size="sm" variant="outline" disabled={!canEditLines} onClick={() => setLines((prev) => [...prev, createEmptyLine()])}>
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Add line
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[920px] text-sm">
                  <thead className="bg-muted/70">
                    <tr className="text-left">
                      <th className="w-[260px] px-3 py-2 font-medium">Charge Code</th>
                      <th className="px-3 py-2 font-medium">Description</th>
                      <th className="w-24 px-3 py-2 font-medium">Qty</th>
                      <th className="w-28 px-3 py-2 font-medium">Rate</th>
                      <th className="w-24 px-3 py-2 font-medium">Tax %</th>
                      <th className="w-28 px-3 py-2 text-right font-medium">Amount</th>
                      <th className="w-16 px-3 py-2 text-right font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeLines.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">No lines.</td>
                      </tr>
                    ) : activeLines.map((line) => {
                      const lineAmount = asNumber(line.quantity) * asNumber(line.unitPrice);
                      const locked = !canEditLines;
                      return (
                        <tr key={line.id} className="border-t">
                          <td className="px-3 py-2">
                            <Select
                              disabled={locked || chargeCodesLoading}
                              value={line.chargeCodeId || undefined}
                              onValueChange={(value) => {
                                const chargeCode = chargeCodeOptions.find((option) => option.id === value);
                                const nextDescription = chargeCode?.description?.trim();
                                updateLine(line.id, {
                                  chargeCodeId: value,
                                  description: nextDescription && (!line.description.trim() || line.description === 'Service line' || line.description === config.documentLabel)
                                    ? nextDescription
                                    : line.description,
                                });
                              }}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder={chargeCodesLoading ? 'Loading...' : 'Select charge'} />
                              </SelectTrigger>
                              <SelectContent>
                                {chargeCodeOptions.map((option) => (
                                  <SelectItem key={option.id} value={option.id}>
                                    {chargeCodeLabel(option)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2">
                            <Input disabled={locked} value={line.description} onChange={(event) => updateLine(line.id, { description: event.target.value })} />
                          </td>
                          <td className="px-3 py-2">
                            <Input disabled={locked} className="text-right" type="number" value={line.quantity} onChange={(event) => updateLine(line.id, { quantity: event.target.value })} />
                          </td>
                          <td className="px-3 py-2">
                            <Input disabled={locked} className="text-right" type="number" value={line.unitPrice} onChange={(event) => updateLine(line.id, { unitPrice: event.target.value })} />
                          </td>
                          <td className="px-3 py-2">
                            <Input disabled={locked} className="text-right" type="number" value={line.taxRate} onChange={(event) => updateLine(line.id, { taxRate: event.target.value })} />
                          </td>
                          <td className="px-3 py-2 text-right font-medium">{money(lineAmount + lineAmount * asNumber(line.taxRate) / 100, editor.currency)}</td>
                          <td className="px-3 py-2 text-right">
                            <Button disabled={locked} variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => removeLine(line.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-5 border-t px-4 py-3 text-sm">
                <span className="text-muted-foreground">Subtotal {money(draftSubtotal, editor.currency)}</span>
                <span className="text-muted-foreground">Tax {money(draftTax, editor.currency)}</span>
                <span className="font-semibold">Total {money(draftSubtotal + draftTax, editor.currency)}</span>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0 space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: SimpleAccountingConfig['accent'];
}) {
  return (
    <div className={cn('rounded-md border bg-background px-4 py-3', tone ? ACCENT_CLASS[tone] : '')}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}
